import {
    useBillingStatusQuery,
    useBusinessInfo,
    useUserInfoQuery,
} from '../../lib/Queries';
import useBusinessPostApi from '../../lib/useBusinessPostApi.js';
import * as Toast from '../../lib/Toast.js';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { ucfirst } from '../../lib/Utils.js';
import { formatDateLong } from '../../lib/DateTime.js';
import InfoMessageBlock from '../InfoMessageBlock.jsx';
import BillingStatus from './BillingStatus.jsx';
import { useFreeBusinessExceededLimits } from '~/lib/BusinessHelpers.js';

export const PricingButton = ({ plan, children }) => {
    const { save: getCheckoutUrl, isSaving } =
        useBusinessPostApi(`/billing/checkout`);

    function checkout(plan) {
        getCheckoutUrl({
            plan: plan,
        }).then(result => {
            window.location.href = result.url;
        });
    }

    return (
        <button className="btn-pill block my-2" onClick={() => checkout(plan)}>
            {isSaving ? 'Loading...' : children}
        </button>
    );
};

const Plan = ({
    title,
    isSelected,
    isOnTrial,
    price,
    features,
    button,
    beforeFeatures,
    className,
}) => {
    return (
        <div
            className={`flex flex-col rounded-lg border-2 bg-white w-full md:h-[480px] ${isSelected ? 'border-primary-700' : 'border-transparent'} p-6 ${className}`}
        >
            <div className="flex-1">
                <h1>{title}</h1>
                <div className="text-gray-500 mt-2 mb-8">{price}</div>
                <div className="">
                    {!!beforeFeatures && (
                        <div className="font-semibold mb-3">
                            {beforeFeatures}
                        </div>
                    )}

                    <div className="mb-4">
                        {features.map((feature, idx) => (
                            <div
                                className={`flex leading-1 ${idx > 0 ? 'mt-3' : ''}`}
                            >
                                <CheckCircleIcon className="text-primary-700 w-6 h-6 mr-2" />
                                {feature}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {isSelected && (
                <div className="text-green-600 rounded px-6 py-3 leading-1 text-base font-semibold w-full flex items-center justify-center">
                    {isOnTrial ? 'Current Trial' : 'Current Plan'}
                </div>
            )}
            {(!isSelected || isOnTrial) && button}
        </div>
    );
};

const PricingTable = ({
    hasBillingHistory,
    showCheckoutLinks,
    billingStatusData,
}) => {
    const { save: saveSwitchToFree, isSaving } = useBusinessPostApi(
        `/billing/downgrade-free`,
    );
    const { data: businessData, refetch } = useBusinessInfo();

    const currentPlan = businessData?.business?.plan;
    const isOnTrial = !!businessData?.business?.is_trial;

    function switchToFree() {
        saveSwitchToFree().then(() => {
            Toast.success('Successfully downgraded to Free');
            refetch();
        });
    }

    return (
        <div>
            <div className="panel-label">Plans</div>
            <div className="mb-4">
                You are currently on
                {isOnTrial && ` a trial of`} the{' '}
                <span className="font-bold">{ucfirst(currentPlan)}</span> plan
                {isOnTrial &&
                    ` that expires ${formatDateLong(businessData?.business?.expires_at)}`}
            </div>

            <div className="flex flex-wrap md:flex-nowrap gap-4 items-start">
                <Plan
                    title="Free"
                    isSelected={currentPlan === 'free'}
                    price="$0/month"
                    className=""
                    features={[
                        '10 clients per month',
                        'Unlimited forms',
                        'Unlimited bookings',
                        '100 emails and texts per month combined',
                    ]}
                    button={
                        showCheckoutLinks && (
                            <button
                                className="btn-light-pill block my-2"
                                onClick={switchToFree}
                            >
                                Switch to Free
                            </button>
                        )
                    }
                />

                <Plan
                    title="Business"
                    price={`$7/month per user${currentPlan === 'business' && billingStatusData?.users > 1 ? ` x ${billingStatusData?.users} users` : ''}`}
                    isSelected={currentPlan === 'business'}
                    className=""
                    beforeFeatures="Everything in Free, plus:"
                    isOnTrial={isOnTrial}
                    features={[
                        'Unlimited clients',
                        '3,000 emails per month',
                        '200 texts per month',
                    ]}
                    button={
                        showCheckoutLinks && (
                            <PricingButton plan="business_monthly">
                                Sign up
                            </PricingButton>
                        )
                    }
                />

                {/*<Plan*/}
                {/*    title="Enterprise"*/}
                {/*    price="Starts at $350/month"*/}
                {/*    isSelected={currentPlan === 'enterprise'}*/}
                {/*    beforeFeatures="Everything in Business, plus:"*/}
                {/*    features={[*/}
                {/*        //'7 users included',*/}
                {/*        //'$20/month per additional user',*/}
                {/*        '75,000 emails and texts per month',*/}
                {/*        'Unlimited businesses',*/}
                {/*        'Guided setup',*/}
                {/*    ]}*/}
                {/*    button={*/}
                {/*        showCheckoutLinks && (*/}
                {/*            <PricingButton plan="enterprise_monthly">*/}
                {/*                Enterprise ($350/month)*/}
                {/*            </PricingButton>*/}
                {/*        )*/}
                {/*    }*/}
                {/*/>*/}
            </div>
            <div className="text-sm text-gray-500 mt-2">
                Each additional paid user includes +1,000 more emails and +200 texts
                per month
            </div>
        </div>
    );
};

const BillingSettings = ({}) => {
    const { data: userData } = useUserInfoQuery();
    const { data: businessData } = useBusinessInfo();
    const { data: billingStatusData } = useBillingStatusQuery();
    const { isExceeded, overage} = useFreeBusinessExceededLimits();

    const { save: getPortalUrl, isSaving } =
        useBusinessPostApi(`/billing/portal`);

    function gotoPortal() {
        getPortalUrl().then(result => {
            document.location.href = result.url;
        });
    }

    const hasBillingHistory = !!businessData?.hasBillingHistory;
    const showCheckoutLinks =
        !hasBillingHistory ||
        !!businessData?.business?.is_trial ||
        businessData?.business?.plan === 'free';

    return (
        <div>
            {hasBillingHistory && (
                <div className="mb-6">
                    <button className="btn-pill" onClick={gotoPortal}>
                        Manage Billing
                    </button>

                    <div className="mt-2 text-sm text-gray-500">
                        View invoices, cancel, and change your plan.
                    </div>
                </div>
            )}

            <div className="max-w-4xl">
                {isExceeded && (
                    <InfoMessageBlock className="mb-4">
                        <div className="mb-2">You have reached the max number of clients allowed on the free
                        plan.</div>
                        {overage < 10 && (
                            <div className="mb-2">You're in a grace period, clients and forms will continue to work normally
                                but if you continue to exceed the limit you will need to upgrade.</div>
                        )}
                        <PricingButton plan="business_monthly">
                            Upgrade Now
                        </PricingButton>
                    </InfoMessageBlock>
                )
                }
                <BillingStatus billingStatusData={billingStatusData} />

                <PricingTable
                    currentPlan={businessData?.business?.plan}
                    hasBillingHistory={hasBillingHistory}
                    showCheckoutLinks={showCheckoutLinks}
                    billingStatusData={billingStatusData}
                />
            </div>
        </div>
    );
};

export default BillingSettings;
